import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";

export interface Service {
  id: string;
  name: string;
  slug: string;
  shortName: string;
  description: string;
  price: number;
  promoPrice: number;
  duration: string;
  image: string;
  benefits: string[];
}

export interface CoreValue {
  id: number;
  title: string;
  description: string;
  icon: string;
}

export interface Benefit {
  id: number;
  title: string;
  description: string;
  icon: string;
}

export interface Faq {
  id: number;
  question: string;
  answer: string;
}

export interface ProcessStep {
  id: number;
  title: string;
  description: string;
  icon: string;
}

interface Result<T> {
  data: T[];
  loading: boolean;
  error: string;
  reload: () => void;
}

// Stable mapper functions (module-level) so the generic hook's
// useCallback dependencies never change between renders.
const mapService = (row: Record<string, unknown>): Service => ({
  id: row.id as string,
  name: row.name as string,
  slug: row.slug as string,
  shortName: row.short_name as string,
  description: row.description as string,
  price: row.price as number,
  promoPrice: row.promo_price as number,
  duration: row.duration as string,
  image: row.image as string,
  benefits: (row.benefits as string[]) ?? [],
});

const mapCoreValue = (row: Record<string, unknown>): CoreValue => ({
  id: row.id as number,
  title: row.title as string,
  description: row.description as string,
  icon: row.icon as string,
});

const mapBenefit = (row: Record<string, unknown>): Benefit => ({
  id: row.id as number,
  title: row.title as string,
  description: row.description as string,
  icon: row.icon as string,
});

const mapFaq = (row: Record<string, unknown>): Faq => ({
  id: row.id as number,
  question: row.question as string,
  answer: row.answer as string,
});

const mapProcessStep = (row: Record<string, unknown>): ProcessStep => ({
  id: row.id as number,
  title: row.title as string,
  description: row.description as string,
  icon: row.icon as string,
});

function useTable<T>(
  table: string,
  map: (row: Record<string, unknown>) => T,
): Result<T> {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data: rows, error: fetchError } = await supabase
        .from(table)
        .select("*")
        .order("sort_order", { ascending: true });

      if (fetchError) {
        throw fetchError;
      }
      setData(rows ? rows.map((r) => map(r as Record<string, unknown>)) : []);
    } catch {
      setError("Không thể tải dữ liệu. Vui lòng thử lại sau.");
    } finally {
      setLoading(false);
    }
  }, [table, map]);

  useEffect(() => {
    void load();
  }, [load]);

  return { data, loading, error, reload: () => void load() };
}

export function useServices(): Result<Service> {
  return useTable<Service>("services", mapService);
}

export function useCoreValues(): Result<CoreValue> {
  return useTable<CoreValue>("core_values", mapCoreValue);
}

export function useBenefits(): Result<Benefit> {
  return useTable<Benefit>("benefits", mapBenefit);
}

export function useFaqs(): Result<Faq> {
  return useTable<Faq>("faqs", mapFaq);
}

export function useProcessSteps(): Result<ProcessStep> {
  return useTable<ProcessStep>("process_steps", mapProcessStep);
}